import React, { useCallback, useMemo, useState } from 'react'
import { User } from '../../models/User'
import { INTEGRATIONS_WITH_SEGMENTED_CONTACT_CHANGE, IntegrationServiceId } from '../../common/integrations'
import {
  ExternalDocumentContactUpdateProps,
  ExternalEmailContactUpdateProps,
  ExternalMeetingContactUpdateProps,
  useInitializeContactChange,
  useSectionNavigation,
} from './hooks'
import { useTracking } from '../../apps/professional-app/fa/common/tracking/hooks'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import {
  CONTACT_UPDATE_STEP,
  selectContact,
  selectContactUpdateStep,
  selectIsLoadingContactChange,
  setContactUpdateStep,
  setCreateNewCRMContact,
  setExternalId,
} from '../../store/meetingSummary/contactUpdate'
import { TrackEvent } from '../../apps/professional-app/fa/common/tracking'
import { getIntegrationUserTrackingDetails } from '../../apps/professional-app/fa/tracking'
import { UpdateExternalContactSelectStep } from './InitCreateOrUpdateExternalContact/UpdateExternalContactSelectStep'
import { UpdateExternalContactModal } from './UpdateExternalContactModal'
import { UpdateExternalContactModalInitialization } from './InitCreateOrUpdateExternalContact/UpdateExternalContactModalInitialization'
import { selectRoomSession } from '../../store/meetingSummary/roomSession'
import { selectSessionMeetingTemplate } from '../../store/meetingSummary/meetingTemplates'
import { SegmentedExternalContactUpdateBody } from './SegmentedExternalContactUpdateBody'
import { UpdateExternalContactModalFooter } from './UpdateExternalContactModalFooter'
import { DefaultExternalContactUpdateBody } from './DefaultExternalContactUpdateBody'
import { NavigationSidebarSection } from '../../components/NavigationWithSideBar/NavigationSidebar'
import { DataProcessingView } from './InitCreateOrUpdateExternalContact/DataProcessing/DataProcessingView'
import { IntegrationSavedModalBody } from '../../components/Integrations/IntegrationSavedModalBody/IntegrationSavedModalBody'
import { useSyncStatuses } from '../../apps/professional-app/fa/MeetingSummary/ExternalUpdates/hooks'
import { DocumentFile } from '../../models/Document'

type documentContext = { type: 'document'; documentId: string; files: DocumentFile[] }
type emailContext = { type: 'email'; emailConnector: string; threadId: string }
type meetingContext = { type: 'meeting'; roomId: string; sessionId: string; meetingTemplateId: string }

type UpdateContext = documentContext | emailContext | meetingContext

interface UniversalModalProps {
  user: User
  serviceId: IntegrationServiceId
  show: boolean
  onClose: () => void
  context: UpdateContext
}

const useUpdateProps = (context: UpdateContext) => {
  return useMemo((): ExternalDocumentContactUpdateProps | ExternalEmailContactUpdateProps | ExternalMeetingContactUpdateProps => {
    switch (context.type) {
      case 'document':
        return { type: 'document', documentId: context.documentId, files: context.files }
      case 'email':
        return { type: 'email', emailConnector: context.emailConnector, threadId: context.threadId }
      case 'meeting':
        return {
          type: 'meeting',
          roomId: context.roomId,
          sessionId: context.sessionId,
          meetingTemplateId: context.meetingTemplateId,
        }
    }
  }, [context])
}

const useContextTracking = (context: UpdateContext, serviceId: string) => {
  const { trackingClient } = useTracking()
  const contact = useAppSelector(selectContact)

  const baseEventDetails = useMemo(() => {
    switch (context.type) {
      case 'document':
        return { documentId: context.documentId }
      case 'email':
        return { emailConnector: context.emailConnector, threadId: context.threadId }
      case 'meeting':
        return { roomId: context.roomId, sessionId: context.sessionId }
    }
  }, [context])

  const trackFieldSelection = (selectedKey: string, isUnselect?: boolean) => {
    const details = baseEventDetails
    trackingClient.track({
      eventName: TrackEvent.CRM_CONTACT_UPDATE_FIELD_SELECTED,
      eventDetails: { ...details, selectedKey },
    })

    if (isUnselect) {
      trackingClient.track({
        eventName: TrackEvent.CONTACT_UPDATE_SECTIONS_UNCHECKED,
        eventDetails: { ...details, serviceId, selectedKey },
      })
    }
  }

  const trackSave = (onSaveCallback: () => void, userId: string) => {
    const details = baseEventDetails
    const meetingDetails = context.type === 'meeting' ? { meetingTemplateId: context.meetingTemplateId } : {}
    const integrationDetails =
      context.type === 'document'
        ? { targetSystem: serviceId }
        : {
            ...getIntegrationUserTrackingDetails(contact),
            userId,
          }

    const eventName = context.type === 'document' ? TrackEvent.DOCUMENT_CONTACT_UPDATED : TrackEvent.CRM_CONTACT_UPDATE_SAVED

    trackingClient.track({
      eventName,
      eventDetails: { ...details, ...meetingDetails, ...integrationDetails },
    })

    onSaveCallback()
  }

  const trackRerun = (userId: string) => {
    if (context.type === 'document') {
      return
    }
    const details = baseEventDetails
    const meetingDetails = context.type === 'meeting' ? { meetingTemplateId: context.meetingTemplateId } : {}

    trackingClient.track({
      eventName: TrackEvent.CRM_CONTACT_UPDATE_RERUN,
      eventDetails: {
        ...details,
        ...meetingDetails,
        ...getIntegrationUserTrackingDetails(contact),
        userId,
      },
    })
  }

  return { trackFieldSelection, trackSave, trackRerun }
}

interface UniversalBodyContentProps {
  user: User
  serviceId: IntegrationServiceId
  context: UpdateContext
  onCancel: () => void
  activeSectionId?: string
  onActiveSectionIdChange?: (id: string) => void
  onSectionsLoaded?: (sections: NavigationSidebarSection[]) => void
}

const UniversalBodyContent: React.FC<UniversalBodyContentProps> = ({
  user,
  serviceId,
  context,
  onCancel,
  activeSectionId,
  onActiveSectionIdChange,
  onSectionsLoaded,
}) => {
  const contactUpdateStep = useAppSelector(selectContactUpdateStep)

  const isLoadingContactChange = useAppSelector(selectIsLoadingContactChange)

  const updateProps = useUpdateProps(context)
  const { trackFieldSelection } = useContextTracking(context, serviceId)

  useInitializeContactChange({ serviceId, user, updateProps, onCancel })

  const isSegmented = INTEGRATIONS_WITH_SEGMENTED_CONTACT_CHANGE.includes(serviceId)
  const ExternalContactUpdateView = isSegmented ? SegmentedExternalContactUpdateBody : DefaultExternalContactUpdateBody

  return (
    <div className={`d-flex flex-column zocks-gap-1.25 p-0 overflow-auto h-100`}>
      {contactUpdateStep === CONTACT_UPDATE_STEP.CONTACT_SELECT_STEP && (
        <UpdateExternalContactSelectStep serviceId={serviceId} onCancel={onCancel} user={user} />
      )}

      {contactUpdateStep === CONTACT_UPDATE_STEP.CONTACT_UPDATE_STEP && (
        <>
          {isLoadingContactChange ? (
            <DataProcessingView serviceId={serviceId} />
          ) : (
            <ExternalContactUpdateView
              serviceId={serviceId}
              onKeySelected={(key: string, isUnselect: boolean | undefined) => trackFieldSelection(key, !!isUnselect)}
              activeSectionId={activeSectionId}
              onActiveSectionIdChange={onActiveSectionIdChange}
              onSectionsLoaded={onSectionsLoaded}
              readonly={false}
              enableExplainer={context.type === 'meeting'}
            />
          )}
        </>
      )}
    </div>
  )
}

interface UniversalFooterProps extends UniversalModalProps {
  onSave: () => void
  onNext: () => void
  onPrevious: () => void
  canNext: boolean
  canPrevious: boolean
  onRerun?: () => void
  onBeforeSave: () => void
}

const UniversalFooterContent: React.FC<UniversalFooterProps> = ({
  serviceId,
  user,
  context,
  onClose,
  onSave,
  onNext,
  onPrevious,
  canNext,
  canPrevious,
  onRerun,
  onBeforeSave,
}) => {
  const updateProps = useUpdateProps(context)
  const { trackSave, trackRerun } = useContextTracking(context, serviceId)

  const rerunWithTracking = (user: User, context: UpdateContext) => {
    if (context.type !== 'document') {
      trackRerun(user.id)
    }
    onRerun?.()
  }

  return (
    <UpdateExternalContactModalFooter
      serviceId={serviceId}
      user={user}
      updateProps={updateProps}
      onSave={() => trackSave(onSave, user.id)}
      onRerun={() => rerunWithTracking(user, context)}
      onCancel={onClose}
      onNext={onNext}
      onPrevious={onPrevious}
      canNext={canNext}
      canPrevious={canPrevious}
      onBeforeSave={onBeforeSave}
    />
  )
}

const UniversalUpdateExternalContact: React.FC<UniversalModalProps> = (props) => {
  const { user, serviceId, show, onClose } = props
  const dispatch = useAppDispatch()
  const contactUpdateStep = useAppSelector(selectContactUpdateStep)
  const [showSuccess, setShowSuccess] = useState<boolean>(false)
  const [syncTime, setSyncTime] = useState<Date | null>(null)

  const integration = useMemo(() => user.integrations?.find((e) => e.serviceId === serviceId), [user.integrations, serviceId])

  const { activeSection, setActiveSection, sections, handleSectionsLoaded, handleNext, handlePrevious, canNext, canPrevious } = useSectionNavigation()

  const handleActiveSectionIdChange = (id: string) => {
    const section = sections.find((s) => s.id === id)
    if (section) {
      setActiveSection(section)
    }
  }

  const handleOnSave = useCallback(() => {
    setShowSuccess(true)
  }, [])

  const handleBeforeSave = useCallback(() => {
    setSyncTime(new Date())
  }, [])

  if (integration && show && contactUpdateStep !== CONTACT_UPDATE_STEP.CONTACT_UPDATE_STEP) {
    dispatch(setContactUpdateStep(CONTACT_UPDATE_STEP.CONTACT_UPDATE_STEP))
    dispatch(setExternalId(integration.externalId))
    dispatch(setCreateNewCRMContact(false))
  }

  const { contactUpdateSyncStatus, contactCreateSyncStatus } = useSyncStatuses()
  const combinedSyncStatuses = useMemo(
    () => [...contactUpdateSyncStatus, ...contactCreateSyncStatus].sort((a, b) => new Date(b.syncedAt).getTime() - new Date(a.syncedAt).getTime()),
    [contactUpdateSyncStatus, contactCreateSyncStatus],
  )

  const BodyComponent = showSuccess ? (
    <IntegrationSavedModalBody
      serviceId={serviceId}
      syncStatuses={combinedSyncStatuses}
      onHide={onClose}
      showSuggestions={props.context.type === 'meeting'}
      syncTime={syncTime}
      fullHeight
    />
  ) : (
    <>
      <UpdateExternalContactModalInitialization serviceId={serviceId} />
      <UniversalBodyContent
        {...props}
        onCancel={onClose}
        activeSectionId={activeSection?.id}
        onActiveSectionIdChange={handleActiveSectionIdChange}
        onSectionsLoaded={handleSectionsLoaded}
      />
    </>
  )

  const FooterComponent = (
    <UniversalFooterContent
      {...props}
      onSave={handleOnSave}
      onBeforeSave={handleBeforeSave}
      onNext={handleNext}
      onPrevious={handlePrevious}
      canNext={canNext}
      canPrevious={canPrevious}
    />
  )
  return (
    <UpdateExternalContactModal
      user={user}
      serviceId={serviceId}
      show={show}
      onCancel={onClose}
      BodyComponent={BodyComponent}
      FooterComponent={!showSuccess && contactUpdateStep === CONTACT_UPDATE_STEP.CONTACT_UPDATE_STEP ? FooterComponent : null}
    />
  )
}

interface UpdateExternalDocumentContactModalProps {
  user: User
  serviceId: IntegrationServiceId
  documentId: string
  files: DocumentFile[]
  show: boolean
  onClose: () => void
}

export const UpdateExternalDocumentContactModal: React.FC<UpdateExternalDocumentContactModalProps> = ({ documentId, files, ...props }) => {
  const context = useMemo<documentContext>(() => ({ type: 'document', documentId, files }), [documentId, files])

  return <UniversalUpdateExternalContact {...props} context={context} />
}

interface UpdateExternalEmailContactModalProps {
  user: User
  serviceId: IntegrationServiceId
  emailConnector: string
  threadId: string
  show: boolean
  onClose: () => void
}

export const UpdateExternalEmailContactModal: React.FC<UpdateExternalEmailContactModalProps> = ({ emailConnector, threadId, ...props }) => {
  const context = useMemo<emailContext>(() => ({ type: 'email', emailConnector, threadId }), [emailConnector, threadId])
  return <UniversalUpdateExternalContact {...props} context={context} />
}

interface UpdateExternalMeetingContactModalProps {
  user: User
  serviceId: IntegrationServiceId
  show: boolean
  onClose: () => void
}

export const UpdateExternalMeetingContactModal: React.FC<UpdateExternalMeetingContactModalProps> = (props) => {
  const roomSession = useAppSelector(selectRoomSession)
  const sessionMeetingTemplate = useAppSelector(selectSessionMeetingTemplate)

  const context = useMemo<meetingContext | null>(() => {
    if (!roomSession || !sessionMeetingTemplate) {
      return null
    }
    return {
      type: 'meeting',
      roomId: roomSession.roomId,
      sessionId: roomSession.id,
      meetingTemplateId: sessionMeetingTemplate.id,
    }
  }, [roomSession, sessionMeetingTemplate])

  if (!context) {
    return null
  }

  return <UniversalUpdateExternalContact {...props} context={context} />
}
